import { useQuery } from '@tanstack/react-query';

import {
  awaitJSON,
  pollS3,
  StreamingLambdaFetch,
  StreamingOrdersLambdaFetch,
  StreamingV2LambdaFetch
} from '@src/utils/fetch-utils';

import {
  MailerInfo,
  NetworkBuyingOptions,
  NetworkOrder,
  Order,
  OrderInfo,
  ShowTitleOption,
  TagInfo
} from './streaming-buying-types';
import { orderPdfFilename, SendViaType } from './send-order-utils';

export const useOrderInfo = (company: string): ReturnType<typeof useQuery<OrderInfo[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingBuyingOrderInfo', company],
    enabled: Boolean(company),
    queryFn: async (): Promise<OrderInfo[]> =>
      awaitJSON<OrderInfo[]>(
        await StreamingOrdersLambdaFetch('/order_info', { params: { company } })
      )
  });

export const useTagInfo = (company: string): ReturnType<typeof useQuery<TagInfo[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingBuyingTagInfo', company],
    enabled: Boolean(company),
    queryFn: async (): Promise<TagInfo[]> =>
      awaitJSON<TagInfo[]>(await StreamingOrdersLambdaFetch('/tag_info', { params: { company } }))
  });

// Disabled for the "new" order route — that order is synthesized client-side, never fetched.
//
// Opts out of the app-wide 5-minute staleTime: `useLocalStorageOrder` compares the order's
// revision against the one it persisted and discards local edits when they diverge, so a
// stale cached order would hide another buyer's save and silently keep doomed local changes.
export const useStreamingOrder = (orderID: string): ReturnType<typeof useQuery<Order>> =>
  useQuery({
    queryKey: ['streamingBuyingOrder', orderID],
    enabled: Boolean(orderID) && orderID !== 'new',
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: async (): Promise<Order> =>
      awaitJSON<Order>(await StreamingOrdersLambdaFetch('/order', { params: { id: orderID } }))
  });

export const useNetworkBuyingOptions = (
  company: string,
  network: string
): ReturnType<typeof useQuery<NetworkBuyingOptions>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingBuyingNetworkInfo', company, network],
    enabled: Boolean(company && network),
    queryFn: async (): Promise<NetworkBuyingOptions> =>
      awaitJSON<NetworkBuyingOptions>(
        await StreamingV2LambdaFetch('/network_buying_info', { params: { company, network } })
      )
  });

export const usePodcastShowTitles = (
  network: string
): ReturnType<typeof useQuery<ShowTitleOption[]>> =>
  useQuery({
    queryKey: ['streamingBuyingPodcasts', network],
    enabled: Boolean(network),
    queryFn: async (): Promise<ShowTitleOption[]> => {
      const showTitles = await awaitJSON<string[]>(
        await StreamingV2LambdaFetch('/getPodcastsByNetwork', { params: { network } })
      );
      return showTitles.map(showTitle => ({ value: showTitle, label: showTitle }));
    }
  });

// Every order for the company, keyed by network — the move-flight picker filters this down.
export const useCompanyOrders = (
  company: string
): ReturnType<typeof useQuery<Record<string, NetworkOrder[]>>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingBuyingCompanyOrders', company],
    enabled: Boolean(company),
    queryFn: async (): Promise<Record<string, NetworkOrder[]>> =>
      awaitJSON<Record<string, NetworkOrder[]>>(
        await StreamingOrdersLambdaFetch('/orders', { params: { company } })
      )
  });

export const useMailerInfo = (
  company: string,
  network: string,
  platforms: string[]
): ReturnType<typeof useQuery<MailerInfo>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['streamingBuyingMailer', company, network, platforms.join(',')],
    enabled: Boolean(company && network && platforms.length),
    queryFn: async (): Promise<MailerInfo> =>
      awaitJSON<MailerInfo>(
        await StreamingV2LambdaFetch('/mailer', {
          params: { company, network, platforms: platforms.join(',') }
        })
      )
  });

// ---- imperative writes (caller refetches the relevant query on success) ----

export const saveOrder = async (body: Record<string, unknown>): Promise<void> => {
  await StreamingOrdersLambdaFetch('/orders', { method: 'POST', body });
};

export const createOrder = async (body: Record<string, unknown>): Promise<number> => {
  const res = await StreamingOrdersLambdaFetch('/orders', { method: 'PUT', body });
  return awaitJSON<number>(res);
};

export const setOrderStatus = async (id: number | undefined, status: number): Promise<void> => {
  await StreamingOrdersLambdaFetch('/orders/status', { method: 'POST', body: { id, status } });
};

// Makes an order's IO and resolves its filename once it's in bpm-streaming-orders. For an agent
// order the server signs it as the signed-in person and records it ready to send.
export const makeOrderPdf = async ({
  id,
  fullName,
  email,
  via
}: {
  id: number;
  fullName: string;
  email: string;
  via: SendViaType;
}): Promise<string> => {
  const filename = orderPdfFilename(id, new Date());
  await StreamingLambdaFetch('/create_streaming_order', {
    method: 'POST',
    body: { order_id: String(id), fullName, email, filename, v2: true, via }
  });
  await pollS3({
    bucket: 'bpm-streaming-orders',
    mimeType: 'application/pdf',
    filename,
    autoDownload: false
  });
  return filename;
};

export const insertPodcastsByNetwork = async (
  network: string,
  newShowTitles: (string | undefined)[]
): Promise<void> => {
  await StreamingV2LambdaFetch('/insertPodcastByNetwork', {
    method: 'POST',
    body: { network, newShowTitles }
  });
};
